import React from 'react';
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { CalendarRange } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './daterangepicker.module.css';
import { useSelector, useDispatch } from 'react-redux';
import { setDateRange } from '../../../redux/actions/dateTimeActions';
import { getValidTz } from '../../../utils/dateUtils';


const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    className={styles.dateRangePickerInput}
    onClick={onClick}
    value={value}
    placeholder={placeholder}
    ref={ref}
    readOnly
  />
));

const CustomDaterangepicker = () => {
  const dispatch = useDispatch();
  const { startDate, endDate } = useSelector((state) => state.dateTime.dateRange);

  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const tz = getValidTz();


  const toTzDate = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };


  const getMaxDate = () => {
    if (!tz) return new Date();
    const dateStr = new Date().toLocaleDateString('en-CA', { timeZone: tz });
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const maxDate = getMaxDate();

  // Default range: maxDate and 7 days before it
  const getDefaultRange = () => {
    const end = maxDate;
    const start = new Date(end);
    start.setDate(start.getDate() - 7);
    return [start, end];
  };

  // Local state to track UI selections
  const [localDates, setLocalDates] = useState(() => {
    if (startDate && endDate) {
      return [toTzDate(startDate), toTzDate(endDate)];
    }
    return getDefaultRange();
  });

  // Sync local state when Redux date changes (external reset, reload, etc.)
  useEffect(() => {
    if (startDate && endDate) {
      setLocalDates([toTzDate(startDate), toTzDate(endDate)]);
    } else {
      setLocalDates(getDefaultRange());
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside, true);

    return () => {
      document.removeEventListener("pointerdown", handleOutside, true);
    };
  }, []);

  const handleDateChange = (dates) => {
    setLocalDates(dates);

    const [start, end] = dates;
    if (start && end) {
      const payload = {
        startDate: dayjs(start).format('YYYY-MM-DD'),
        endDate: dayjs(end).format('YYYY-MM-DD'),
      };
      dispatch(setDateRange(payload));
    }
  };

  return (
    <div ref={wrapperRef} className={`nopan nodrag ${styles.dateInputWrapper}`}>
      <CalendarRange className={styles.calendarIcon} />
      <DatePicker
        open={isOpen}
        onInputClick={() => setIsOpen(true)}
        onClickOutside={() => setIsOpen(false)}
        selectsRange
        startDate={localDates[0]}
        endDate={localDates[1]}
        onChange={handleDateChange}
        dateFormat="MMMM d, yyyy"
        placeholderText="Select date range"
        customInput={<CustomInput />}
        shouldCloseOnSelect={false}
        maxDate={maxDate}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        isClearable={false}
      />
    </div>
  );
};

export default CustomDaterangepicker;
